import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { storage } from '../utils/storage';

interface AttendanceRecord {
  id: string;
  userId: string;
  workDate: Date;
  sequence: number;
  type: 'REGULAR' | 'OVERTIME';
  checkIn: Date;
  checkOut: Date | null;
  totalHours: number;
  nightHours: number;
  isLate: boolean;
  isEarlyLeave: boolean;
  isAbsent: boolean;
  note: string | null;
  user: {
    id: string;
    name: string;
    email: string;
  };
}

interface TeamMember {
  id: string;
  email: string;
  name: string;
  role: 'COMPANY_ADMIN' | 'TEAM_LEADER' | 'TEAM_MEMBER';
  teamId: string;
  emailVerified: boolean;
  isActive: boolean;
  createdAt: string;
  team: {
    id: string;
    name: string;
  };
}

interface Team {
  id: string;
  name: string;
  inviteCode: string;
  isActive: boolean;
  createdAt: string;
  _count: {
    members: number;
  };
}

interface RoleChangeHistory {
  id: string;
  userId: string;
  companyId: string;
  teamId: string | null;
  changedBy: string;
  oldRole: string;
  newRole: string;
  reason: string | null;
  createdAt: string;
  changer: {
    id: string;
    name: string;
    email: string;
  };
}

interface TeamStats {
  period: {
    start: Date;
    end: Date;
  };
  summary: {
    totalMembers: number;
    avgAttendanceRate: number;
    totalLate: number;
    totalEarlyLeave: number;
    totalOvertime: number;
    avgWorkHours: number;
  };
  members: Array<{
    userId: string;
    name: string;
    email: string;
    totalDays: number;
    totalHours: number;
    lateCount: number;
    earlyLeaveCount: number;
    overtimeCount: number;
    attendanceRate: number;
  }>;
}

const AdminPage: React.FC = () => {
  const { user } = useAuth();
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [filteredRecords, setFilteredRecords] = useState<AttendanceRecord[]>([]);
  const [teamStats, setTeamStats] = useState<TeamStats | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [teams, setTeams] = useState<Team[]>([]); // 팀 목록
  const [swipedTeamId, setSwipedTeamId] = useState<string | null>(null); // 스와이프 상태
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]); // 체크박스
  
  // 팀 생성 모달
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [creatingTeam, setCreatingTeam] = useState(false);
  
  // 팀명 수정 모달
  const [showEditTeamModal, setShowEditTeamModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [editTeamName, setEditTeamName] = useState('');
  const [updatingTeam, setUpdatingTeam] = useState(false);
  
  // 역할 변경 히스토리 모달
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [roleHistory, setRoleHistory] = useState<RoleChangeHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // 날짜 필터 (localStorage에서 복원)
  const today = format(new Date(), 'yyyy-MM-dd');
  const [startDate, setStartDate] = useState(
    storage.get('admin-startDate') || today
  );
  const [endDate, setEndDate] = useState(
    storage.get('admin-endDate') || today
  );

  // 항목별 필터
  const [selectedNames, setSelectedNames] = useState<string[]>([]);
  const [selectedType, setSelectedType] = useState<string>('all');
  const [checkInHour, setCheckInHour] = useState<string>('all');
  const [checkOutHour, setCheckOutHour] = useState<string>('all');
  const [hoursFilter, setHoursFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    if (user?.role === 'TEAM_LEADER' || user?.role === 'COMPANY_ADMIN') {
      loadRecords();
      loadTeamStats();
      loadTeamMembers(); // 팀원 목록 로드!
      loadTeams(); // 팀 목록 로드! ← NEW
    }
  }, [user]);

  useEffect(() => {
    applyFilters();
  }, [records, selectedNames, selectedType, checkInHour, checkOutHour, hoursFilter, statusFilter]);

  const loadRecords = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      storage.set('admin-startDate', startDate);
      storage.set('admin-endDate', endDate);

      const response = await api.get('/attendance/admin/all', { params });
      setRecords(response.data.data.records);
    } catch (error: any) {
      console.error('출근부 조회 실패:', error);
      // 데이터 없음은 에러가 아니므로 토스트 제거
      if (error.response?.status !== 404) {
        toast.error(error.response?.data?.message || '출근부 조회 실패');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadTeamStats = async () => {
    try {
      const params: any = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await api.get('/attendance/admin/team-stats', { params });
      setTeamStats(response.data.data);
    } catch (error: any) {
      console.error('팀 통계 조회 실패:', error);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const response = await api.get('/users/team-members');
      setTeamMembers(response.data.data);
    } catch (error: any) {
      console.error('팀원 목록 조회 실패:', error);
      toast.error('팀원 목록을 불러올 수 없습니다.');
    }
  };

  const handleRoleChange = async (userId: string, newRole: 'TEAM_MEMBER' | 'TEAM_LEADER') => {
    const roleName = newRole === 'TEAM_LEADER' ? '팀장' : '팀원';
    
    if (!window.confirm(`정말 이 팀원을 ${roleName}으로 변경하시겠습니까?`)) {
      return;
    }

    try {
      await api.patch(`/users/${userId}/role`, { role: newRole });
      toast.success('역할이 변경되었습니다! ✅');
      loadTeamMembers(); // 목록 새로고침
    } catch (error: any) {
      console.error('역할 변경 실패:', error);
      toast.error(error.response?.data?.message || '역할 변경에 실패했습니다.');
    }
  };

  const handleStatusChange = async (userId: string, isActive: boolean) => {
    const action = isActive ? '활성화' : '비활성화';
    
    if (!window.confirm(`정말 이 팀원을 ${action}하시겠습니까?`)) {
      return;
    }

    try {
      await api.patch(`/users/${userId}/status`, { isActive });
      toast.success(`팀원이 ${action}되었습니다! ✅`);
      loadTeamMembers(); // 목록 새로고침
    } catch (error: any) {
      console.error('상태 변경 실패:', error);
      toast.error(error.response?.data?.message || '상태 변경에 실패했습니다.');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('정말 이 계정을 삭제하시겠습니까? 복구할 수 없습니다.')) {
      return;
    }

    try {
      await api.delete(`/users/${userId}`);
      toast.success('팀원이 삭제되었습니다! 🗑️');
      loadTeamMembers(); // 목록 새로고침
    } catch (error: any) {
      console.error('계정 삭제 실패:', error);
      toast.error(error.response?.data?.message || '계정 삭제에 실패했습니다.');
    }
  };

  const loadRoleHistory = async (memberId: string) => {
    setLoadingHistory(true);
    try {
      const response = await api.get(`/users/${memberId}/role-history`);
      setRoleHistory(response.data.data);
    } catch (error: any) {
      console.error('히스토리 조회 실패:', error);
      toast.error(error.response?.data?.message || '히스토리 조회 실패');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleShowHistory = (member: TeamMember) => {
    setSelectedMember(member);
    setShowHistoryModal(true);
    loadRoleHistory(member.id);
  };

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) {
      toast.error('팀 이름을 입력해주세요.');
      return;
    }

    setCreatingTeam(true);
    try {
      const response = await api.post('/teams', { name: newTeamName });
      const { team, message } = response.data.data;
      
      toast.success(message, { duration: 5000 });

      // 초대 코드 클립보드 복사 (별도 try-catch — Capacitor 환경 호환성)
      try {
        await navigator.clipboard.writeText(team.inviteCode);
        toast.success('초대 코드가 클립보드에 복사되었습니다! 📋');
      } catch (clipErr) {
        console.error('클립보드 복사 실패 (Capacitor 환경?):', clipErr);
        toast.success(`초대 코드: ${team.inviteCode}`, { duration: 10000 });
      }

      // 모달 닫기
      setShowTeamModal(false);
      setNewTeamName('');

      // 팀 목록 새로고침 (새 팀 반영)
      loadTeams();
      loadTeamMembers();
    } catch (error: any) {
      console.error('팀 생성 실패:', error);
      toast.error(error.response?.data?.message || '팀 생성에 실패했습니다.');
    } finally {
      setCreatingTeam(false);
    }
  };

  const loadTeams = async () => {
    try {
      const response = await api.get('/teams');
      setTeams(response.data.data);
    } catch (error: any) {
      console.error('팀 목록 조회 실패:', error);
      toast.error(error.response?.data?.message || '팀 목록 조회 실패');
    }
  };

  const handleDeleteTeam = async (teamId: string) => {
    if (!window.confirm('정말 이 팀을 삭제하시겠습니까? 팀원들은 팀 없이 남게 됩니다.')) {
      return;
    }

    try {
      await api.delete(`/teams/${teamId}`);
      toast.success('팀이 삭제되었습니다! 🗑️');
      loadTeams();
      loadTeamMembers();
      setSwipedTeamId(null);
    } catch (error: any) {
      console.error('팀 삭제 실패:', error);
      toast.error(error.response?.data?.message || '팀 삭제에 실패했습니다.');
    }
  };

  const handleShowEditTeam = (team: Team) => {
    setEditingTeam(team);
    setEditTeamName(team.name);
    setShowEditTeamModal(true);
  };

  const handleUpdateTeamName = async () => {
    if (!editingTeam) return;

    if (!editTeamName.trim()) {
      toast.error('팀 이름을 입력해주세요.');
      return;
    }

    if (!window.confirm(`팀명을 "${editTeamName}"(으)로 변경하시겠습니까?`)) {
      return;
    }

    setUpdatingTeam(true);
    try {
      const response = await api.patch(`/teams/${editingTeam.id}/name`, { name: editTeamName });
      toast.success(response.data.message || '팀명이 변경되었습니다! ✅');
      
      // 모달 닫기
      setShowEditTeamModal(false);
      setEditingTeam(null);
      setEditTeamName('');
      
      // 목록 새로고침
      loadTeams();
      loadTeamMembers();
    } catch (error: any) {
      console.error('팀명 수정 실패:', error);
      toast.error(error.response?.data?.message || '팀명 수정에 실패했습니다.');
    } finally {
      setUpdatingTeam(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...records];

    if (selectedNames.length > 0) {
      filtered = filtered.filter(r => selectedNames.includes(r.user.name));
    }

    if (selectedType === 'regular') {
      filtered = filtered.filter(r => r.type === 'REGULAR');
    } else if (selectedType === 'ot') {
      filtered = filtered.filter(r => r.type === 'OVERTIME');
    }

    if (checkInHour !== 'all') {
      filtered = filtered.filter(r => {
        const hour = new Date(r.checkIn).getHours();
        return hour === parseInt(checkInHour);
      });
    }

    if (checkOutHour !== 'all') {
      filtered = filtered.filter(r => {
        if (!r.checkOut) return false;
        const hour = new Date(r.checkOut).getHours();
        return hour === parseInt(checkOutHour);
      });
    }

    if (hoursFilter === 'gte') {
      filtered = filtered.filter(r => r.totalHours >= 7.9);
    } else if (hoursFilter === 'lt') {
      filtered = filtered.filter(r => r.totalHours < 7.9);
    }

    if (statusFilter === 'normal') {
      filtered = filtered.filter(r => !r.isLate);
    } else if (statusFilter === 'late') {
      filtered = filtered.filter(r => r.isLate);
    }

    setFilteredRecords(filtered);
  };

  const handleBulkApprove = async (approved: boolean) => {
    if (selectedIds.length === 0) {
      toast.error('선택된 기록이 없습니다');
      return;
    }

    if (!window.confirm(approved ? `${selectedIds.length}건을 승인하시겠습니까?` : `${selectedIds.length}건을 거부하시겠습니까?`)) {
      return;
    }

    try {
      await api.post('/attendance/admin/bulk-approve', {
        recordIds: selectedIds,
        approved,
      });
      toast.success(approved ? 'OT 승인 완료' : 'OT 거부 완료');
      setSelectedIds([]);
      loadRecords();
    } catch (error: any) {
      toast.error(error.response?.data?.message || '일괄 처리 실패');
    }
  };

  const handleDelete = async (recordId: string) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) {
      return;
    }

    try {
      await api.delete(`/attendance/admin/${recordId}`);
      toast.success('출근 기록 삭제 완료');
      loadRecords();
    } catch (error: any) {
      toast.error(error.response?.data?.message || '삭제 실패');
    }
  };

  const toggleNameFilter = (name: string) => {
    if (selectedNames.includes(name)) {
      setSelectedNames(selectedNames.filter(n => n !== name));
    } else {
      setSelectedNames([...selectedNames, name]);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredRecords.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredRecords.map(r => r.id));
    }
  };

  const toggleSelect = (id: string) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const resetFilters = () => {
    setSelectedNames([]);
    setSelectedType('all');
    setCheckInHour('all');
    setCheckOutHour('all');
    setHoursFilter('all');
    setStatusFilter('all');
  };

  const downloadCSV = () => {
    // CSV 헤더
    const headers = ['날짜', '이름', '구분', '출근', '퇴근', '근무시간', '상태', '비고'];
    
    // CSV 데이터
    const rows = filteredRecords.map(r => [
      format(new Date(r.workDate), 'yyyy-MM-dd (EEE)', { locale: ko }),
      r.user.name,
      r.sequence === 1 ? '기본근무' : `OT ${r.sequence - 1}차`,
      format(new Date(r.checkIn), 'HH:mm'),
      r.checkOut ? format(new Date(r.checkOut), 'HH:mm') : '-',
      `${r.totalHours.toFixed(1)}h`,
      r.isLate ? '지각' : r.isEarlyLeave ? '조퇴' : '정상',
      r.note || '',
    ]);

    // CSV 문자열 생성
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    // BOM 추가 (엑셀 한글 깨짐 방지)
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // 다운로드
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `AZOCSII_출근부_${startDate}_${endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('CSV 다운로드 완료');
  };

  if (user?.role !== 'TEAM_LEADER' && user?.role !== 'COMPANY_ADMIN') {
    return (
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto text-center">
          <div className="text-6xl mb-4">🚫</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            관리자 전용 페이지
          </h1>
          <p className="text-gray-600">
            관리자 권한이 필요합니다.
          </p>
        </div>
      </main>
    );
  }

  const uniqueNames = Array.from(new Set(records.map(r => r.user.name)));

  return (
    <main className="container mx-auto px-4 py-6 pb-0">
      <div className="max-w-6xl mx-auto">
        {/* 헤더 */}
        <div className="text-center mb-4">
          <h1 className="text-xl font-bold text-primary mb-1">
            👑 관리자 페이지
          </h1>
          <p className="text-xs text-gray-600">
            팀 전체 출근 관리
          </p>
        </div>

        {/* 초대 코드 카드 - 개선된 버전 */}
        <div className="card-azocsii mb-4 p-4">
          <h3 className="text-base font-bold text-primary mb-3">🔑 초대 코드 관리</h3>
          
          {/* 범례 */}
          <div className="flex gap-2 mb-3 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-purple-500 rounded"></div>
              <span className="text-gray-600">회사 관리자용</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-blue-500 rounded"></div>
              <span className="text-gray-600">팀원 가입용</span>
            </div>
            {user?.role === 'COMPANY_ADMIN' && (
              <div className="flex items-center gap-1 ml-auto">
                <span className="text-gray-500">← 밀어서 삭제</span>
              </div>
            )}
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {/* 회사 코드 — COMPANY_ADMIN 전용 */}
            {user?.role === 'COMPANY_ADMIN' && (
              <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-purple-600">🏢 회사 코드</span>
                  <button
                    onClick={() => {
                      if (user?.company?.inviteCode) {
                        navigator.clipboard.writeText(user.company.inviteCode);
                        toast.success('회사 코드 복사 완료! 📋');
                      }
                    }}
                    className="px-2 py-1 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700"
                  >
                    복사
                  </button>
                </div>
                <div className="text-lg font-black text-purple-800 font-mono">
                  {user?.company?.inviteCode || '-'}
                </div>
                <div className="text-xs text-purple-600 mt-1">
                  회사 관리자 전용 (새 팀 만들 수 있음)
                </div>
              </div>
            )}

            {/* 팀 코드 목록 (스와이프 가능) */}
            {teams.map((team) => {
              const isSwiped = swipedTeamId === team.id;
              const isCurrentTeam = user?.teamId === team.id;

              return (
                <div
                  key={team.id}
                  className="relative overflow-hidden"
                  onTouchStart={(e) => {
                    if (user?.role !== 'COMPANY_ADMIN') return;
                    const touch = e.touches[0];
                    (e.currentTarget as any).startX = touch.clientX;
                  }}
                  onTouchMove={(e) => {
                    if (user?.role !== 'COMPANY_ADMIN') return;
                    const touch = e.touches[0];
                    const startX = (e.currentTarget as any).startX;
                    const diff = startX - touch.clientX;
                    if (diff > 50) {
                      setSwipedTeamId(team.id);
                    } else if (diff < -50) {
                      setSwipedTeamId(null);
                    }
                  }}
                  onTouchEnd={() => {
                    // 스와이프 유지
                  }}
                >
                  {/* 삭제 버튼 배경 */}
                  {user?.role === 'COMPANY_ADMIN' && isSwiped && (
                    <div className="absolute inset-0 bg-red-500 flex items-center justify-end pr-4">
                      <button
                        onClick={() => handleDeleteTeam(team.id)}
                        className="text-white font-bold"
                      >
                        🗑️ 삭제
                      </button>
                    </div>
                  )}

                  {/* 팀 카드 */}
                  <div
                    className={`bg-blue-50 p-3 rounded-lg border border-blue-200 transition-transform ${
                      isSwiped ? '-translate-x-20' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-blue-600">
                          👥 {team.name}
                        </span>
                        {isCurrentTeam && (
                          <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded">
                            내 팀
                          </span>
                        )}
                        <span className="text-xs text-gray-500">
                          ({team._count.members}명)
                        </span>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(team.inviteCode);
                            toast.success(`${team.name} 코드 복사 완료! 📋`);
                          }}
                          className="px-2 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700"
                        >
                          복사
                        </button>
                        {user?.role === 'COMPANY_ADMIN' && (
                          <button
                            onClick={() => handleShowEditTeam(team)}
                            className="px-2 py-1 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700"
                          >
                            ✏️ 수정
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="text-lg font-black text-blue-800 font-mono">
                      {team.inviteCode}
                    </div>
                    <div className="text-xs text-blue-600 mt-1">
                      팀원 가입 시 이 코드 입력
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-3 p-2 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600">
              💡 팀원에게 <strong>팀 코드</strong>를 공유하면 회원가입 시 자동으로 해당 팀에 합류해요!
            </p>
          </div>
        </div>

        {/* 팀 생성 버튼 (COMPANY_ADMIN만) */}
        {user?.role === 'COMPANY_ADMIN' && (
          <div className="card-azocsii mb-4 p-4">
            <h3 className="text-base font-bold text-primary mb-3">➕ 새 팀 만들기</h3>
            <p className="text-sm text-gray-600 mb-3">
              팀을 추가로 생성하면 각 팀마다 고유한 초대 코드가 발급됩니다.
            </p>
            <button
              onClick={() => setShowTeamModal(true)}
              className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-bold rounded-lg hover:shadow-lg transition-all"
            >
              ➕ 팀 생성하기
            </button>
          </div>
        )}

        {/* 팀원 관리 (COMPANY_ADMIN + TEAM_LEADER) */}
        {(user?.role === 'COMPANY_ADMIN' || user?.role === 'TEAM_LEADER') && teamMembers.length > 0 && (
          <div className="card-azocsii mb-4 p-4">
            <h3 className="text-base font-bold text-primary mb-3">👥 팀원 관리</h3>
            
            <div className="space-y-2">
              {teamMembers
                .filter(member => {
                  // 본인 제외
                  if (member.id === user?.id) {
                    return false;
                  }
                  // TEAM_LEADER는 COMPANY_ADMIN 제외
                  if (user?.role === 'TEAM_LEADER' && member.role === 'COMPANY_ADMIN') {
                    return false;
                  }
                  return true;
                })
                .map((member) => (
                <div key={member.id} className={`p-3 rounded-lg border ${
                  member.isActive 
                    ? 'bg-gray-50 border-gray-200' 
                    : 'bg-gray-100 border-gray-300 opacity-60'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="font-bold text-sm text-gray-800">{member.name}</div>
                        {!member.isActive && (
                          <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded">
                            비활성
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-600">{member.email}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        팀: {member.team.name}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-xs font-bold mb-2 ${
                        member.role === 'COMPANY_ADMIN' ? 'text-purple-600' :
                        member.role === 'TEAM_LEADER' ? 'text-blue-600' :
                        'text-gray-600'
                      }`}>
                        {member.role === 'COMPANY_ADMIN' ? '👑 회사 관리자' :
                         member.role === 'TEAM_LEADER' ? '⭐ 팀장' :
                         '일반 사용자'}
                      </div>
                      
                      {/* 역할 변경 버튼 (COMPANY_ADMIN만, 본인 제외) */}
                      {user?.role === 'COMPANY_ADMIN' && member.role !== 'COMPANY_ADMIN' && member.id !== user?.id && (
                        <div className="space-y-1">
                          {/* 역할 변경 */}
                          <div className="flex gap-1">
                            {member.role === 'TEAM_MEMBER' && (
                              <button
                                onClick={() => handleRoleChange(member.id, 'TEAM_LEADER')}
                                className="px-2 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700"
                                disabled={!member.isActive}
                              >
                                ⭐ 팀장으로
                              </button>
                            )}
                            {member.role === 'TEAM_LEADER' && (
                              <button
                                onClick={() => handleRoleChange(member.id, 'TEAM_MEMBER')}
                                className="px-2 py-1 bg-gray-600 text-white text-xs rounded-lg hover:bg-gray-700"
                                disabled={!member.isActive}
                              >
                                팀원으로
                              </button>
                            )}
                          </div>
                          
                          {/* 상태 변경 */}
                          <button
                            onClick={() => handleStatusChange(member.id, !member.isActive)}
                            className={`w-full px-2 py-1 text-white text-xs rounded-lg ${
                              member.isActive 
                                ? 'bg-red-500 hover:bg-red-600' 
                                : 'bg-green-500 hover:bg-green-600'
                            }`}
                          >
                            {member.isActive ? '🚫 비활성화' : '✅ 활성화'}
                          </button>
                          
                          {/* 삭제 버튼 (비활성화된 계정만) */}
                          {!member.isActive && (
                            <button
                              onClick={() => handleDeleteUser(member.id)}
                              className="w-full px-2 py-1 bg-gray-800 text-white text-xs rounded-lg hover:bg-black"
                            >
                              🗑️ 삭제
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>가입일: {format(new Date(member.createdAt), 'yyyy.MM.dd')}</span>
                    
                    {/* 히스토리 버튼 (모든 관리자) */}
                    {(user?.role === 'COMPANY_ADMIN' || user?.role === 'TEAM_LEADER') && (
                      <button
                        onClick={() => handleShowHistory(member)}
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        📜 히스토리
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 팀 전체 통계 대시보드 */}
        {teamStats && (
          <div className="card-azocsii mb-4 p-4">
            <h3 className="text-base font-bold text-primary mb-3">📊 팀 전체 통계</h3>
            
            {/* 요약 */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="bg-blue-50 p-2 rounded-lg">
                <div className="text-xs text-gray-600">평균 출근율</div>
                <div className="text-lg font-bold text-blue-600">
                  {teamStats.summary.avgAttendanceRate}%
                </div>
              </div>
              <div className="bg-orange-50 p-2 rounded-lg">
                <div className="text-xs text-gray-600">총 지각</div>
                <div className="text-lg font-bold text-orange-600">
                  {teamStats.summary.totalLate}회
                </div>
              </div>
              <div className="bg-purple-50 p-2 rounded-lg">
                <div className="text-xs text-gray-600">총 OT</div>
                <div className="text-lg font-bold text-purple-600">
                  {teamStats.summary.totalOvertime}회
                </div>
              </div>
            </div>

            {/* 팀원별 상세 */}
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="p-1.5 text-left">이름</th>
                    <th className="p-1.5 text-left">출근일</th>
                    <th className="p-1.5 text-left">근무시간</th>
                    <th className="p-1.5 text-left">지각</th>
                    <th className="p-1.5 text-left">OT</th>
                  </tr>
                </thead>
                <tbody>
                  {teamStats.members.map((member) => (
                    <tr key={member.userId} className="border-b">
                      <td className="p-1.5 font-medium">{member.name}</td>
                      <td className="p-1.5">{member.totalDays}일</td>
                      <td className="p-1.5">{member.totalHours}h</td>
                      <td className="p-1.5">
                        <span className={member.lateCount > 0 ? 'text-red-600 font-bold' : ''}>
                          {member.lateCount}회
                        </span>
                      </td>
                      <td className="p-1.5">{member.overtimeCount}회</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 날짜 필터 */}
        <div className="card-azocsii mb-4 p-4">
          <h3 className="text-base font-bold text-primary mb-3">📅 조회 기간</h3>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">시작일</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border-2 border-gray-300 rounded-lg focus:outline-none focus:border-accent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">종료일</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border-2 border-gray-300 rounded-lg focus:outline-none focus:border-accent"
              />
            </div>
          </div>
          <button
            onClick={() => {
              loadRecords();
              loadTeamStats();
            }}
            className="w-full mt-2 btn-azocsii py-2 text-sm"
          >
            🔍 조회
          </button>
        </div>

        {/* 필터 섹션 */}
        {records.length > 0 && (
          <div className="card-azocsii mb-4 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-primary">🎯 필터</h3>
              <button
                onClick={resetFilters}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                초기화
              </button>
            </div>

            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">이름</label>
              <div className="flex flex-wrap gap-1">
                {uniqueNames.map(name => (
                  <button
                    key={name}
                    onClick={() => toggleNameFilter(name)}
                    className={`px-2 py-1 rounded text-xs ${
                      selectedNames.includes(name)
                        ? 'bg-accent text-white'
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">구분</label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border-2 border-gray-300 rounded-lg"
                >
                  <option value="all">전체</option>
                  <option value="regular">기본근무</option>
                  <option value="ot">OT (전체)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">상태</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border-2 border-gray-300 rounded-lg"
                >
                  <option value="all">전체</option>
                  <option value="normal">정상</option>
                  <option value="late">지각</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">출근</label>
                <select
                  value={checkInHour}
                  onChange={(e) => setCheckInHour(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border-2 border-gray-300 rounded-lg"
                >
                  <option value="all">전체</option>
                  {[16, 17, 18, 19, 20].map(h => (
                    <option key={h} value={h}>{h}시대</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">퇴근</label>
                <select
                  value={checkOutHour}
                  onChange={(e) => setCheckOutHour(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border-2 border-gray-300 rounded-lg"
                >
                  <option value="all">전체</option>
                  {[0, 1, 2, 3, 4].map(h => (
                    <option key={h} value={h}>{h}시대</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">근무시간</label>
              <select
                value={hoursFilter}
                onChange={(e) => setHoursFilter(e.target.value)}
                className="w-full px-2 py-1.5 text-xs border-2 border-gray-300 rounded-lg"
              >
                <option value="all">전체</option>
                <option value="gte">7.9h 이상</option>
                <option value="lt">7.9h 미만</option>
              </select>
            </div>
          </div>
        )}

        {/* 일괄 처리 버튼 */}
        {selectedIds.length >= 2 && (
          <div className="card-azocsii mb-4 p-3 bg-yellow-50 border-2 border-yellow-300">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-yellow-800">
                {selectedIds.length}건 선택됨
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => handleBulkApprove(true)}
                  className="px-3 py-1.5 bg-gradient-to-r from-green-400 via-emerald-400 to-teal-400 text-white rounded-lg text-xs font-medium shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all"
                >
                  ✓ 일괄 승인
                </button>
                <button
                  onClick={() => handleBulkApprove(false)}
                  className="px-3 py-1.5 bg-gradient-to-r from-red-400 via-rose-400 to-pink-400 text-white rounded-lg text-xs font-medium shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all"
                >
                  ✗ 일괄 거부
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 출근부 테이블 */}
        {loading ? (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">⏳</div>
            <p className="text-gray-600">로딩 중...</p>
          </div>
        ) : filteredRecords.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <div className="text-6xl mb-4">📋</div>
            <p className="text-gray-600">출근 기록이 없습니다</p>
          </div>
        ) : (
          <div className="card-azocsii overflow-x-auto p-3">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-primary">
                📊 출근부 ({filteredRecords.length}건)
              </h3>
              <button
                onClick={downloadCSV}
                className="px-3 py-1.5 bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400 text-white rounded-lg text-xs font-medium shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all"
              >
                📥 CSV 다운로드
              </button>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-100">
                  <th className="p-1.5">
                    <input
                      type="checkbox"
                      checked={selectedIds.length === filteredRecords.length}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th className="p-1.5 text-left">날짜</th>
                  <th className="p-1.5 text-left">이름</th>
                  <th className="p-1.5 text-left">구분</th>
                  <th className="p-1.5 text-left">출근</th>
                  <th className="p-1.5 text-left">퇴근</th>
                  <th className="p-1.5 text-left">시간</th>
                  <th className="p-1.5 text-left">상태</th>
                  <th className="p-1.5 text-left">관리</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map((record) => (
                  <tr key={record.id} className="border-b hover:bg-gray-50">
                    <td className="p-1.5">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(record.id)}
                        onChange={() => toggleSelect(record.id)}
                      />
                    </td>
                    <td className="p-1.5 whitespace-nowrap">
                      {format(new Date(record.workDate), 'MM/dd (EEE)', { locale: ko })}
                    </td>
                    <td className="p-1.5 font-medium">{record.user.name}</td>
                    <td className="p-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-xs ${
                        record.type === 'REGULAR' 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-purple-100 text-purple-700'
                      }`}>
                        {record.sequence === 1 ? '기본근무' : `OT ${record.sequence - 1}차`}
                      </span>
                    </td>
                    <td className="p-1.5">
                      {format(new Date(record.checkIn), 'HH:mm')}
                    </td>
                    <td className="p-1.5">
                      {record.checkOut ? format(new Date(record.checkOut), 'HH:mm') : '-'}
                    </td>
                    <td className="p-1.5">{record.totalHours.toFixed(1)}h</td>
                    <td className="p-1.5">
                      <div className="flex gap-0.5">
                        {record.isLate && (
                          <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">지각</span>
                        )}
                        {record.isEarlyLeave && (
                          <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">조퇴</span>
                        )}
                        {!record.isLate && !record.isEarlyLeave && (
                          <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">정상</span>
                        )}
                      </div>
                    </td>
                    <td className="p-1.5">
                      <button
                        onClick={() => handleDelete(record.id)}
                        className="text-xs bg-gradient-to-r from-gray-400 to-slate-400 text-white px-1.5 py-0.5 rounded hover:from-gray-500 hover:to-slate-500 transition-all"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 팀 생성 모달 */}
      {showTeamModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-xl font-bold text-primary mb-4">➕ 새 팀 만들기</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                팀 이름
              </label>
              <input
                type="text"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                placeholder="예: 영업1팀"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                disabled={creatingTeam}
              />
            </div>

            <div className="bg-blue-50 p-3 rounded-lg mb-4">
              <p className="text-xs text-blue-600">
                💡 팀 생성 시 초대 코드가 자동으로 발급되고, 클립보드에 복사됩니다!
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowTeamModal(false);
                  setNewTeamName('');
                }}
                className="flex-1 py-3 bg-gray-200 text-gray-700 font-bold rounded-lg hover:bg-gray-300"
                disabled={creatingTeam}
              >
                취소
              </button>
              <button
                onClick={handleCreateTeam}
                className="flex-1 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-bold rounded-lg hover:shadow-lg disabled:opacity-50"
                disabled={creatingTeam}
              >
                {creatingTeam ? '생성 중...' : '팀 생성'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 팀명 수정 모달 */}
      {showEditTeamModal && editingTeam && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-xl font-bold text-primary mb-4">✏️ 팀명 수정</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                현재 팀명: {editingTeam.name}
              </label>
              <input
                type="text"
                value={editTeamName}
                onChange={(e) => setEditTeamName(e.target.value)}
                placeholder="새 팀 이름 입력"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                disabled={updatingTeam}
                autoFocus
              />
            </div>

            <div className="bg-yellow-50 p-3 rounded-lg mb-4">
              <p className="text-xs text-yellow-700">
                ⚠️ 이 팀에 속한 모든 팀원에게 변경된 팀명이 자동으로 반영됩니다.
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowEditTeamModal(false);
                  setEditingTeam(null);
                  setEditTeamName('');
                }}
                className="flex-1 py-3 bg-gray-200 text-gray-700 font-bold rounded-lg hover:bg-gray-300"
                disabled={updatingTeam}
              >
                취소
              </button>
              <button
                onClick={handleUpdateTeamName}
                className="flex-1 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold rounded-lg hover:shadow-lg disabled:opacity-50"
                disabled={updatingTeam}
              >
                {updatingTeam ? '수정 중...' : '✏️ 수정'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 역할 변경 히스토리 모달 */}
      {showHistoryModal && selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl max-h-[80vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-primary mb-4">
              📜 역할 변경 히스토리
            </h3>

            <div className="bg-gray-50 p-3 rounded-lg mb-4">
              <div className="font-bold text-sm">{selectedMember.name}</div>
              <div className="text-xs text-gray-600">{selectedMember.email}</div>
              <div className="text-xs text-gray-500 mt-1">
                현재 역할: {
                  selectedMember.role === 'COMPANY_ADMIN' ? '👑 회사 관리자' :
                  selectedMember.role === 'TEAM_LEADER' ? '⭐ 팀장' :
                  '일반 사용자'
                }
              </div>
            </div>

            {loadingHistory ? (
              <div className="text-center py-8">
                <div className="text-gray-500">로딩 중...</div>
              </div>
            ) : roleHistory.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-500">역할 변경 기록이 없습니다.</div>
              </div>
            ) : (
              <div className="space-y-3">
                {roleHistory.map((history, index) => (
                  <div key={history.id} className="bg-blue-50 p-3 rounded-lg border border-blue-200 relative">
                    {/* 첫 번째 항목 뱃지 */}
                    {index === 0 && (
                      <span className="absolute top-2 right-2 text-xs bg-blue-600 text-white px-2 py-0.5 rounded">
                        최근
                      </span>
                    )}
                    
                    <div className="text-xs text-gray-600 mb-2">
                      {format(new Date(history.createdAt), 'yyyy.MM.dd HH:mm', { locale: ko })}
                    </div>
                    
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs font-bold px-2 py-1 rounded ${
                        history.oldRole === 'TEAM_MEMBER' ? 'bg-gray-200 text-gray-700' :
                        history.oldRole === 'TEAM_LEADER' ? 'bg-blue-200 text-blue-700' :
                        'bg-purple-200 text-purple-700'
                      }`}>
                        {history.oldRole === 'COMPANY_ADMIN' ? '👑 회사 관리자' :
                         history.oldRole === 'TEAM_LEADER' ? '⭐ 팀장' :
                         '일반 사용자'}
                      </span>
                      <span className="text-gray-500">→</span>
                      <span className={`text-xs font-bold px-2 py-1 rounded ${
                        history.newRole === 'TEAM_MEMBER' ? 'bg-gray-200 text-gray-700' :
                        history.newRole === 'TEAM_LEADER' ? 'bg-blue-200 text-blue-700' :
                        'bg-purple-200 text-purple-700'
                      }`}>
                        {history.newRole === 'COMPANY_ADMIN' ? '👑 회사 관리자' :
                         history.newRole === 'TEAM_LEADER' ? '⭐ 팀장' :
                         '일반 사용자'}
                      </span>
                    </div>
                    
                    <div className="text-xs text-gray-600">
                      변경자: {history.changer.name} ({history.changer.email})
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={() => {
                setShowHistoryModal(false);
                setSelectedMember(null);
                setRoleHistory([]);
              }}
              className="w-full mt-4 py-3 bg-gray-200 text-gray-700 font-bold rounded-lg hover:bg-gray-300"
            >
              닫기
            </button>
          </div>
        </div>
      )}
    </main>
  );
};

export default AdminPage;
